import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../utils/safeToast';
import { 
  Mail, 
  Search, 
  Loader2, 
  RefreshCw, 
  Trash2, 
  Link as LinkIcon,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  Settings
} from 'lucide-react';

const API = "/api";

const EmailAdminPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [emailsRes, statusRes] = await Promise.all([
        axios.get(`${API}/email/unassigned`),
        axios.get(`${API}/email/status`)
      ]);
      setEmails(emailsRes.data.emails || []);
      setTotal(emailsRes.data.total || 0);
      setEmailStatus(statusRes.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Fehler beim Laden der E-Mails');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!emailStatus?.configured) {
      toast.error('Microsoft Graph ist nicht konfiguriert');
      return;
    }
    
    setSyncing(true);
    try {
      await axios.post(`${API}/email/sync`);
      toast.success('E-Mail-Sync gestartet');
      // Wait a bit then refresh
      setTimeout(() => {
        fetchData();
        setSyncing(false);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Sync');
      setSyncing(false);
    }
  };

  const handleAssign = async () => {
    if (!ticketId.trim()) {
      toast.error('Bitte Ticket-ID eingeben');
      return;
    }
    
    setAssigning(true);
    try {
      await axios.post(`${API}/email/unassigned/${selectedEmail.id}/assign?ticket_id=${ticketId}`);
      toast.success(`E-Mail zu ${ticketId} zugeordnet`);
      setAssignDialogOpen(false);
      setSelectedEmail(null);
      setTicketId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Zuordnung');
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/email/unassigned/${selectedEmail.id}`);
      toast.success('E-Mail gelöscht');
      setDeleteDialogOpen(false);
      setSelectedEmail(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEmails = emails.filter(email =>
    email.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.from_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.from_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E4C92]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="email-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            E-Mail Administration
          </h1>
          <p className="text-stone-500 mt-1">Unzugeordnete E-Mails verwalten</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing || !emailStatus?.configured}
            className="rounded-full"
            data-testid="sync-emails-btn"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card className={`border ${emailStatus?.configured ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-full ${emailStatus?.configured ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              {emailStatus?.configured ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-amber-600" />
              )}
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${emailStatus?.configured ? 'text-emerald-800' : 'text-amber-800'}`}>
                {emailStatus?.configured ? 'Microsoft Graph verbunden' : 'Microsoft Graph nicht konfiguriert'}
              </h3>
              <p className="text-sm text-slate-600">
                Postfach: {emailStatus?.mailbox_user || 'Nicht konfiguriert'}
              </p>
              {emailStatus?.last_sync && (
                <p className="text-xs text-slate-500 mt-1">
                  Letzter Sync: {formatDate(emailStatus.last_sync)}
                </p>
              )}
              {emailStatus?.error && !emailStatus?.configured && (
                <p className="text-xs text-amber-700 mt-1">{emailStatus.error}</p>
              )}
            </div>
            {!emailStatus?.configured && (
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                <Settings className="w-3 h-3 mr-1" />
                Setup erforderlich
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="bg-white border border-stone-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="E-Mails durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-50"
                data-testid="search-emails"
              />
            </div>
            <Badge variant="outline" className="px-4 py-2">
              {total} unzugeordnet
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card className="bg-white border border-stone-200">
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#0E4C92]" />
            Unzugeordnete E-Mails
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredEmails.length > 0 ? (
            <div className="space-y-3">
              {filteredEmails.map((email) => (
                <div
                  key={email.id}
                  className="p-4 border border-slate-200 rounded-xl hover:border-[#0E4C92]/30 transition-colors"
                  data-testid={`email-item-${email.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-900">
                          {email.from_name || email.from_email}
                        </span>
                        {email.from_name && (
                          <span className="text-xs text-slate-500">
                            &lt;{email.from_email}&gt;
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-slate-800 truncate">
                        {email.subject || '(Kein Betreff)'}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                        {email.body_preview}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(email.received_at)}
                        </span>
                        {email.potential_ticket_id && (
                          <Badge variant="outline" className="text-xs">
                            Mögliche ID: {email.potential_ticket_id}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedEmail(email);
                          setTicketId(email.potential_ticket_id || '');
                          setAssignDialogOpen(true);
                        }}
                        className="rounded-lg text-[#0E4C92] border-[#0E4C92]"
                        data-testid={`assign-email-${email.id}`}
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        Zuordnen
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedEmail(email);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-email-${email.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Keine unzugeordneten E-Mails</p>
              <p className="text-sm mt-1">
                {emailStatus?.configured 
                  ? 'Alle eingehenden E-Mails wurden automatisch zugeordnet.' 
                  : 'Konfigurieren Sie Microsoft Graph, um E-Mails zu empfangen.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">E-Mail zuordnen</DialogTitle>
            <DialogDescription>
              Ordnen Sie diese E-Mail einem Ticket zu, um sie in den Verlauf aufzunehmen.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="my-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Von:</strong> {selectedEmail.from_name || selectedEmail.from_email}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <strong>Betreff:</strong> {selectedEmail.subject}
              </p>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="ticket-id">Ticket-ID</Label>
            <Input
              id="ticket-id"
              placeholder="T-2026-000001"
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value.toUpperCase())}
              className="font-mono"
              data-testid="ticket-id-input"
            />
            <p className="text-xs text-slate-500">
              Format: T-YYYY-XXXXXX (z.B. T-2026-000001)
            </p>
          </div>
          
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setAssignDialogOpen(false)}
              className="rounded-full"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleAssign}
              disabled={assigning || !ticketId.trim()}
              className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <LinkIcon className="w-4 h-4 mr-2" />
              )}
              Zuordnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>E-Mail löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese E-Mail wird unwiderruflich gelöscht und kann nicht wiederhergestellt werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 rounded-full"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailAdminPage;
